import apiService from './apiService';
import { getImageUrl } from '../utils/imageUrl';

/**
 * Extract raw image path/URL from a category record across common backend field conventions.
 * Never returns hardcoded or placeholder URLs.
 *
 * @param {object} item
 * @returns {string}
 */
export function extractCategoryImagePath(item) {
    if (!item || typeof item !== 'object') return '';

    const candidates = [
        item.image,
        item.image_url,
        item.imageUrl,
        item.category_image,
        item.categoryImage,
        item.thumbnail,
        item.thumbnail_url,
        item.thumbnailUrl,
        item.photo,
    ];

    for (const candidate of candidates) {
        if (!candidate) continue;
        if (typeof candidate === 'string' && candidate.trim()) {
            const trimmed = candidate.trim();
            if (trimmed !== 'null' && trimmed !== 'undefined') {
                return trimmed;
            }
        }
        if (typeof candidate === 'object') {
            const nested =
                candidate.url ||
                candidate.secure_url ||
                candidate.path ||
                candidate.src ||
                '';
            if (typeof nested === 'string' && nested.trim()) {
                return nested.trim();
            }
        }
    }

    return '';
}

/**
 * Resolve a category's dynamic image URL from real API data only.
 * Appends a version timestamp on `/uploads/...` paths when `updatedAt` is present
 * so replaced category images update immediately without stale browser caching.
 *
 * @param {object} item
 * @returns {string|null}
 */
export function resolveDynamicCategoryImage(item) {
    const rawPath = extractCategoryImagePath(item);
    if (!rawPath) return null;

    const resolved = getImageUrl(rawPath);
    if (!resolved) return null;

    const updatedRaw = item?.updatedAt || item?.updated_at;
    if (updatedRaw && resolved.includes('/uploads/') && !resolved.includes('?')) {
        const ts = new Date(updatedRaw).getTime();
        if (Number.isFinite(ts) && ts > 0) {
            return `${resolved}?v=${ts}`;
        }
    }

    return resolved;
}

/**
 * Extract a normalized parent category ID (`string | null`) from an API category item.
 * Handles `parent_id`, `parentId`, or `parent` (as string or populated object `{ _id }`).
 *
 * @param {object} item
 * @returns {string|null}
 */
export function extractParentId(item) {
    if (!item || typeof item !== 'object') return null;

    const selfId = item._id || item.id;
    const raw =
        item.parent_id !== undefined
            ? item.parent_id
            : item.parentId !== undefined
              ? item.parentId
              : item.parent;

    if (raw === null || raw === undefined || raw === false) return null;

    let resolved = null;
    if (typeof raw === 'string' || typeof raw === 'number') {
        const trimmed = String(raw).trim();
        if (
            trimmed &&
            trimmed.toLowerCase() !== 'null' &&
            trimmed.toLowerCase() !== 'undefined'
        ) {
            resolved = trimmed;
        }
    } else if (typeof raw === 'object') {
        const nestedId = raw._id || raw.id;
        if (nestedId !== null && nestedId !== undefined) {
            const trimmed = String(nestedId).trim();
            if (trimmed && trimmed.toLowerCase() !== 'null') {
                resolved = trimmed;
            }
        }
    }

    if (!resolved) return null;
    if (selfId && String(selfId).trim() === resolved) return null;

    return resolved;
}

/**
 * Dynamically build a parent -> subcategory hierarchy from a flat API categories array.
 * - Root/parent categories: items with `parent_id === null` (never includes subcategories).
 * - Subcategories: matched strictly where `String(sub.parent_id) === String(parent._id)`.
 * - Supports arbitrary nesting depth safely with cycle protection.
 *
 * @param {Array} categories
 */
export function buildCategoryHierarchy(categories = []) {
    const allCategories = [];
    const byId = new Map();
    const childrenByParentId = new Map();

    if (Array.isArray(categories)) {
        for (const item of categories) {
            if (!item || typeof item !== 'object') continue;
            const id = String(item._id || item.id || '').trim();
            if (!id || byId.has(id)) continue;

            const parentId = extractParentId(item);
            const normalizedItem =
                item.parent_id === parentId ? item : { ...item, parent_id: parentId };

            allCategories.push(normalizedItem);
            byId.set(id, normalizedItem);
        }
    }

    const parentCategories = [];

    for (const cat of allCategories) {
        if (!cat.parent_id) {
            parentCategories.push(cat);
        } else {
            const pKey = String(cat.parent_id);
            if (!childrenByParentId.has(pKey)) {
                childrenByParentId.set(pKey, []);
            }
            childrenByParentId.get(pKey).push(cat);
        }
    }

    const getCategoryById = (categoryId) => {
        if (!categoryId) return null;
        return byId.get(String(categoryId)) || null;
    };

    const getSubcategories = (parentId) => {
        if (!parentId) return [];
        return childrenByParentId.get(String(parentId)) || [];
    };

    const hasSubcategories = (categoryId) => {
        if (!categoryId) return false;
        const list = childrenByParentId.get(String(categoryId));
        return Array.isArray(list) && list.length > 0;
    };

    const getAncestors = (categoryId) => {
        const trail = [];
        const visited = new Set();
        let current = getCategoryById(categoryId);

        while (current && current.parent_id) {
            const pid = String(current.parent_id);
            if (visited.has(pid)) break;
            visited.add(pid);
            const parentObj = byId.get(pid);
            if (!parentObj) break;
            trail.unshift(parentObj);
            current = parentObj;
        }

        return trail;
    };

    return {
        allCategories,
        parentCategories,
        byId,
        childrenByParentId,
        getCategoryById,
        getSubcategories,
        hasSubcategories,
        getAncestors,
    };
}

/**
 * Ecommerce Category Service
 * Fetches categories from the public /api/ecommerce endpoint (no auth required)
 */
class CategoryService {
    /**
     * Normalize a single category record returned by the API.
     * @param {object} item
     * @returns {object|null}
     */
    normalizeCategory(item) {
        if (!item || typeof item !== 'object') return null;

        const id = item._id || item.id;
        const name = typeof item.name === 'string' ? item.name.trim() : '';
        if (!id || !name) return null;

        const rawStatus = typeof item.status === 'string' ? item.status.trim().toLowerCase() : '';
        const isActive =
            item.is_active !== false &&
            item.isActive !== false &&
            rawStatus !== 'inactive';

        const rawImage = extractCategoryImagePath(item);
        const image = resolveDynamicCategoryImage(item);
        const parentId = extractParentId(item);

        const rawFit = String(
            item.image_fit || item.imageFit || item.object_fit || item.objectFit || 'contain'
        )
            .trim()
            .toLowerCase();
        const imageFit = rawFit === 'cover' ? 'cover' : 'contain';

        const rawOrder = item.display_order ?? item.displayOrder ?? item.order;
        const hasExplicitOrder =
            rawOrder !== undefined &&
            rawOrder !== null &&
            rawOrder !== '' &&
            Number.isFinite(Number(rawOrder));

        return {
            ...item,
            _id: String(id),
            id: String(id),
            name,
            parent_id: parentId,
            image,
            _rawImage: rawImage || null,
            image_fit: imageFit,
            image_alt:
                (typeof (item.image_alt || item.imageAlt || item.alt_text || item.altText) === 'string'
                    ? (item.image_alt || item.imageAlt || item.alt_text || item.altText).trim()
                    : '') || name,
            is_active: isActive,
            _sortOrder: hasExplicitOrder ? Number(rawOrder) : null,
        };
    }

    /**
     * Get categories (optionally only those that have at least one featured product)
     * @param {object} params - e.g. { featured_only: 'true' }
     * @param {object} options - e.g. { forceRefresh: true }
     * @returns {Promise<Array>} Array of normalized category objects
     */
    async getAll(params = {}, options = {}) {
        const queryParams = { ...params };
        if (options?.forceRefresh) {
            queryParams._t = Date.now();
        }

        const response = await apiService.get('/ecommerce/categories', queryParams);

        const candidates = [
            response?.data?.categories,
            response?.categories,
            Array.isArray(response?.data) ? response.data : null,
            Array.isArray(response) ? response : null,
        ];

        const rawList = candidates.find((arr) => Array.isArray(arr)) || [];

        const normalized = rawList
            .map((item, idx) => {
                const cat = this.normalizeCategory(item);
                if (!cat || !cat.is_active) return null;
                return { ...cat, _originalIndex: idx };
            })
            .filter(Boolean);

        const hasAnySortOrder = normalized.some((c) => c._sortOrder !== null);
        if (hasAnySortOrder) {
            normalized.sort((a, b) => {
                const orderA = a._sortOrder !== null ? a._sortOrder : Number.MAX_SAFE_INTEGER;
                const orderB = b._sortOrder !== null ? b._sortOrder : Number.MAX_SAFE_INTEGER;
                if (orderA !== orderB) return orderA - orderB;
                return a._originalIndex - b._originalIndex;
            });
        }

        return normalized.map(({ _sortOrder, _originalIndex, ...rest }) => rest);
    }
}

const categoryService = new CategoryService();
export default categoryService;
